import { useMemo } from "react";
import { _Product } from "./global-stores";
import { PlusIcon } from "./icons";
import { ProductMetaFieldExtraProduct } from "./product";
import { useVariantDetailsModal } from "./use-variant-details-modal";

interface Props {
  product: _Product;
  items?: ProductMetaFieldExtraProduct[];
  selection: ProductMetaFieldExtraProduct;
  namespace: string;
  gridColumnsDesktop?: string;
  gridColumnsMobile?: string;
  onSelect: (namespace, selectedItem: ProductMetaFieldExtraProduct) => void;
  required?: boolean;
}
type GroupedMetaObjects = { [T: string]: ProductMetaFieldExtraProduct[] };

const DESKTOP_COLS_CLASSES = {
  "3": "md:grid-cols-3",
  "4": "md:grid-cols-4",
  "5": "md:grid-cols-5",
  "6": "md:grid-cols-6",
};
const MOBILE_COLS_CLASSES = {
  "1": "grid-cols-1",
  "2": "grid-cols-2",
  "3": "grid-cols-3",
  "4": "grid-cols-4",
};

const getGridClasses = (desktopCols?: string, mobileCols?: string) => {
  const desktop = DESKTOP_COLS_CLASSES[desktopCols || "5"] || "md:grid-cols-5";
  const mobile = MOBILE_COLS_CLASSES[mobileCols || "2"] || "grid-cols-2";
  return `grid ${desktop} ${mobile} gap-4`;
};

export const ProductGroupMetafieldOptionSelector = ({ product, items, selection, namespace, gridColumnsDesktop, gridColumnsMobile, onSelect, required = true }: Props) => {
  const { toggleModal, setProductData } = useVariantDetailsModal();
  const metaObjectsGroups = useMemo(() => {
    const metaObjects = items as ProductMetaFieldExtraProduct[];
    return metaObjects?.reduce<GroupedMetaObjects>((prev, current) => {
      
      if (prev[current.group]) {
        return {
          ...prev,
          [current.group]: [
            ...prev[current.group],
            current,
          ]
        }
      }

      return {
        ...prev,
        [current.group]: [
          current,
        ]
      }
    }, {})
  }, [product]);

  // If this is an optional step and no selection is made, we don't want to show any selection
  const shouldShowSelection = required || selection;

  return (
    <div>
      <div className={"flex flex-col gap-3 mb-1 md:mb-8"}>
        <div className={"relative h-[187px] p-4 flex rounded-lg overflow-hidden border-gray-300 bg-gray-100"}>

          {shouldShowSelection && selection?.imageUrl && (
            <img
              className={"absolute left-0 top-0 bottom-0 right-0 w-full h-full object-cover"}
              width={568}
              height={187}
              src={selection.imageUrl}
              alt={selection.name}
            />
          )}
          
          <div className={"flex flex-col items-center justify-center w-full h-full"}>
            <p className={"text-sm text-gray-500"}>
              {required ? "Please select an option" : "Optional - Select an option if needed"}
            </p>
          </div>
        </div>

        {shouldShowSelection && selection?.name && 
          <div className={"flex flex-col items-end relative flex-1 self-end"}>
            <button
              onClick={() => {
                setProductData({
                  title: selection.name,
                  imageSrc: selection.imageUrl,
                  description: selection.description?.trim() || undefined,
                  composition: selection.composition?.trim() || undefined,
                  durability: selection.durability?.trim() || undefined,
                  careInstructions: selection.careInstructions?.trim() || undefined
                });
                toggleModal(true)
              }}
              className={"text-sm flex items-center justify-start md:justify-center gap-[10px] px-4 py-2 bg-[#DFEFFE] text-primary-bg border-primary-bg border-w-[1px] border-solid rounded-[2px] rounded-tr-none"}
            >
              <span>{selection.name}</span>
              <span className={"shrink-0 text-xs border-b-[1px] border-primary-bg flex items-center gap-1"}>More Info <PlusIcon className={"w-2 h-2"}/></span>
            </button>
          </div>
        }
      </div>
      <div className={"overflow-auto flex flex-col gap-8"}>
        {Object.entries(metaObjectsGroups).map(([key, metaObjects]) => {
          return (
            <div className={"flex flex-col gap-2"}>
            <h4 className={"text-sm sticky z-10 top-0 left-0 right-0 py-2 bg-white"}>{key}</h4>
            <div className={getGridClasses(gridColumnsDesktop, gridColumnsMobile)}>
              {metaObjects?.map((item, index) => {
                const title = item["name"];
                const checked = selection?.name === title;
                const id = `${item["name"]}-${index}`;
                const imageSrc = item["imageUrl"];
                return (<button
                  key={id}
                  type={"button"}
                  className={"flex flex-col gap-2 text-center items-center"} 
                  onClick={() => {
                    onSelect(namespace, item)
                  }}>
                  <div className={`relative overflow-hidden w-full h-auto aspect-square bg-gray-100 rounded-lg border-solid ${ checked ? "border-primary-bg border-[3px]" : "border-gray-300 border-[1px]"}`}>
                    {imageSrc && <img className={"w-full h-auto aspect-1 object-cover"} width={200} height={200} src={imageSrc} alt={imageSrc || ""} />}
                  </div>
                  <p className={"text-sm"}>{title}</p>
                </button>)
              })}
            </div>
          </div>)
        })}
      </div>
        
    </div>
  )
}
